// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const SEO_CHECKLIST = [
  { id: 'title', category: 'On-Page', label: 'Page title tags (50-60 chars)', weight: 10 },
  { id: 'meta', category: 'On-Page', label: 'Meta descriptions (150-160 chars)', weight: 8 },
  { id: 'h1', category: 'On-Page', label: 'Single H1 tag per page', weight: 7 },
  { id: 'headers', category: 'On-Page', label: 'Proper heading hierarchy (H2-H6)', weight: 5 },
  { id: 'alt', category: 'On-Page', label: 'Image alt text on all images', weight: 6 },
  { id: 'internal', category: 'On-Page', label: 'Internal linking structure', weight: 7 },
  { id: 'keywords', category: 'On-Page', label: 'Target keywords in first 100 words', weight: 8 },
  { id: 'ssl', category: 'Technical', label: 'SSL certificate (HTTPS)', weight: 10 },
  { id: 'mobile', category: 'Technical', label: 'Mobile responsive design', weight: 10 },
  { id: 'speed', category: 'Technical', label: 'Page load under 3 seconds', weight: 9 },
  { id: 'sitemap', category: 'Technical', label: 'XML sitemap submitted', weight: 7 },
  { id: 'robots', category: 'Technical', label: 'Robots.txt configured', weight: 5 },
  { id: 'schema', category: 'Technical', label: 'Schema markup / structured data', weight: 8 },
  { id: 'canonical', category: 'Technical', label: 'Canonical URLs set', weight: 6 },
  { id: 'gmb', category: 'Local', label: 'Google Business Profile claimed', weight: 9 },
  { id: 'nap', category: 'Local', label: 'NAP consistency (Name, Address, Phone)', weight: 8 },
  { id: 'citations', category: 'Local', label: 'Local directory citations (20+)', weight: 6 },
  { id: 'reviews', category: 'Local', label: 'Review generation strategy', weight: 7 },
  { id: 'local_keywords', category: 'Local', label: 'Location-based keywords', weight: 7 },
  { id: 'backlinks', category: 'Off-Page', label: 'Quality backlink profile', weight: 9 },
  { id: 'social', category: 'Off-Page', label: 'Social media profiles linked', weight: 5 },
  { id: 'guest', category: 'Off-Page', label: 'Guest posting strategy', weight: 6 },
  { id: 'directories', category: 'Off-Page', label: 'Industry directory listings', weight: 5 },
];

const KEYWORD_DEMO = [
  { keyword: 'custom overhead doors', volume: 2400, difficulty: 42, cpc: '$4.20', position: 8, trend: 'up' },
  { keyword: 'garage door installation near me', volume: 8100, difficulty: 61, cpc: '$8.50', position: 15, trend: 'up' },
  { keyword: 'commercial door repair', volume: 1900, difficulty: 38, cpc: '$6.10', position: 5, trend: 'stable' },
  { keyword: 'overhead door maintenance', volume: 1300, difficulty: 29, cpc: '$3.80', position: 3, trend: 'up' },
  { keyword: 'industrial door systems', volume: 880, difficulty: 35, cpc: '$5.40', position: 12, trend: 'down' },
  { keyword: 'automatic garage door opener', volume: 6600, difficulty: 55, cpc: '$7.20', position: 22, trend: 'stable' },
  { keyword: 'emergency door repair', volume: 3200, difficulty: 44, cpc: '$9.30', position: 7, trend: 'up' },
  { keyword: 'residential garage doors', volume: 4500, difficulty: 48, cpc: '$5.80', position: 11, trend: 'stable' },
];

const COMPETITOR_DOMAINS = [
  { domain: 'competitor-a.com', da: 45, keywords: 340, backlinks: '2.1K', traffic: '12K/mo' },
  { domain: 'competitor-b.com', da: 38, keywords: 210, backlinks: '890', traffic: '6.2K/mo' },
  { domain: 'competitor-c.com', da: 52, keywords: 520, backlinks: '4.3K', traffic: '24K/mo' },
];

const CONTENT_IDEAS = [
  { title: '10 Signs Your Garage Door Needs Replacement', type: 'Blog Post', difficulty: 'Easy', est_traffic: '450/mo', keywords: ['garage door replacement', 'door repair signs'] },
  { title: 'Commercial vs Residential Overhead Doors: Complete Guide', type: 'Pillar Page', difficulty: 'Medium', est_traffic: '1.2K/mo', keywords: ['commercial doors', 'residential doors', 'overhead door types'] },
  { title: 'How Much Does Garage Door Installation Cost in 2026?', type: 'Blog Post', difficulty: 'Hard', est_traffic: '2.8K/mo', keywords: ['garage door cost', 'installation price'] },
  { title: 'DIY Garage Door Maintenance Checklist', type: 'Downloadable', difficulty: 'Easy', est_traffic: '680/mo', keywords: ['door maintenance', 'garage door upkeep'] },
  { title: 'Video: How Automatic Door Openers Work', type: 'Video', difficulty: 'Medium', est_traffic: '1.5K/mo', keywords: ['automatic opener', 'how it works'] },
];

const S = {
  page: { padding: '24px', maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 24 },
  title: { fontSize: 28, fontWeight: 700, color: 'var(--text)', margin: 0 },
  subtitle: { fontSize: 14, color: '#999', marginTop: 4 },
  tabs: { display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' },
  tab: (active) => ({ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: active ? 'var(--accent, #d4a843)' : 'var(--card, #1a1a2e)', color: active ? '#000' : 'var(--text)' }),
  card: { background: 'var(--card, #1a1a2e)', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid var(--border, #2a2a4a)' },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 12 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 },
  stat: { background: 'var(--card, #1a1a2e)', borderRadius: 12, padding: 20, textAlign: 'center', border: '1px solid var(--border, #2a2a4a)' },
  statValue: { fontSize: 28, fontWeight: 800, color: 'var(--accent, #d4a843)' },
  statLabel: { fontSize: 12, color: '#888', marginTop: 4 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--border, #2a2a4a)', color: '#999', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border, #2a2a4a)', color: 'var(--text)' },
  badge: (color) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${color}22`, color }),
  btn: (primary) => ({ padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: primary ? 'var(--accent, #d4a843)' : 'var(--card, #1a1a2e)', color: primary ? '#000' : 'var(--text)', border: primary ? 'none' : '1px solid var(--border, #2a2a4a)' }),
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border, #2a2a4a)', background: 'var(--bg, #0f0f23)', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border, #2a2a4a)', background: 'var(--bg, #0f0f23)', color: 'var(--text)', fontSize: 14, minHeight: 100, resize: 'vertical', boxSizing: 'border-box' },
  progress: (pct, color) => ({ height: 8, borderRadius: 4, background: '#1a1a2e', overflow: 'hidden', position: 'relative' }),
  progressBar: (pct, color) => ({ position: 'absolute', top: 0, left: 0, height: '100%', width: `${pct}%`, borderRadius: 4, background: color || 'var(--accent, #d4a843)', transition: 'width 0.5s ease' }),
  checkItem: (done) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, marginBottom: 6, cursor: 'pointer', background: done ? 'rgba(76, 175, 80, 0.08)' : 'transparent', border: '1px solid', borderColor: done ? '#4caf50' : 'var(--border, #2a2a4a)' }),
  flexRow: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
};

function SEOOptimizerPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('audit');
  const [checklist, setChecklist] = useState({});
  const [siteUrl, setSiteUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDesc, setMetaDesc] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedMeta, setGeneratedMeta] = useState(null);
  const [contentTab, setContentTab] = useState('ideas');

  const toggleCheck = (id) => setChecklist(p => ({ ...p, [id]: !p[id] }));

  const categories = [...new Set(SEO_CHECKLIST.map(c => c.category))];
  const totalWeight = SEO_CHECKLIST.reduce((s, c) => s + c.weight, 0);
  const completedWeight = SEO_CHECKLIST.filter(c => checklist[c.id]).reduce((s, c) => s + c.weight, 0);
  const score = Math.round((completedWeight / totalWeight) * 100);

  const handleScan = () => {
    if (!siteUrl) return;
    setScanning(true);
    setTimeout(() => {
      setScanning(false);
      setScanned(true);
      const autoChecked = {};
      ['ssl', 'mobile', 'robots', 'social'].forEach(id => autoChecked[id] = true);
      setChecklist(autoChecked);
    }, 3000);
  };

  const handleGenerateMeta = () => {
    if (!keywordInput) return;
    setGenerating(true);
    setTimeout(() => {
      setGenerating(false);
      setGeneratedMeta({
        titles: [
          `${keywordInput} - Professional Service | Your Business Name`,
          `Best ${keywordInput} Near You | Trusted Since 2010`,
          `Expert ${keywordInput} Services | Free Estimates Today`,
        ],
        descriptions: [
          `Looking for ${keywordInput.toLowerCase()}? We offer professional, affordable service with 5-star reviews. Get a free estimate today. Serving your area since 2010.`,
          `Top-rated ${keywordInput.toLowerCase()} services. Licensed & insured. Same-day availability. Call now for a free consultation and see why 500+ customers trust us.`,
          `Professional ${keywordInput.toLowerCase()} at competitive prices. Family-owned, locally operated. 100% satisfaction guaranteed. Book online or call today.`,
        ],
      });
    }, 2000);
  };

  const TABS = [
    { id: 'audit', label: 'SEO Audit', icon: '🔍' },
    { id: 'keywords', label: 'Keywords', icon: '🎯' },
    { id: 'content', label: 'Content Strategy', icon: '📝' },
    { id: 'meta', label: 'Meta Generator', icon: '🏷️' },
    { id: 'competitors', label: 'Competitors', icon: '⚔️' },
    { id: 'reports', label: 'Reports', icon: '📊' },
  ];

  return (
    <div style={S.page}>
      <div style={S.header}>
        <h1 style={S.title}>🔍 SEO Optimizer</h1>
        <p style={S.subtitle}>Dominate search rankings — AI-powered SEO strategy, keyword research, and content planning</p>
      </div>

      <div style={S.tabs}>
        {TABS.map(t => (
          <button key={t.id} style={S.tab(tab === t.id)} onClick={() => setTab(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div style={S.grid}>
        <div style={S.stat}>
          <div style={S.statValue}>{score}</div>
          <div style={S.statLabel}>SEO Score</div>
        </div>
        <div style={S.stat}>
          <div style={S.statValue}>{KEYWORD_DEMO.length}</div>
          <div style={S.statLabel}>Tracked Keywords</div>
        </div>
        <div style={S.stat}>
          <div style={S.statValue}>12</div>
          <div style={S.statLabel}>Avg. Position</div>
        </div>
        <div style={S.stat}>
          <div style={S.statValue}>2.4K</div>
          <div style={S.statLabel}>Monthly Organic Traffic</div>
        </div>
      </div>

      {/* SEO Audit Tab */}
      {tab === 'audit' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>Site Scanner</div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <input style={{ ...S.input, flex: 1 }} placeholder="https://yourbusiness.com" value={siteUrl} onChange={e => setSiteUrl(e.target.value)} />
              <button style={S.btn(true)} onClick={handleScan} disabled={scanning}>
                {scanning ? 'Scanning...' : 'Scan Site'}
              </button>
            </div>
            {scanning && (
              <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🔄</div>
                Analyzing your website for SEO issues...
              </div>
            )}
            {scanned && (
              <div style={{ padding: '12px 16px', borderRadius: 8, background: 'rgba(76, 175, 80, 0.1)', border: '1px solid #4caf50', color: '#4caf50', fontSize: 14 }}>
                Scan complete — 4 items auto-detected. Review and complete the checklist below.
              </div>
            )}
          </div>

          <div style={S.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={S.cardTitle}>SEO Checklist</div>
              <div style={{ fontSize: 14, color: '#999' }}>{SEO_CHECKLIST.filter(c => checklist[c.id]).length}/{SEO_CHECKLIST.length} completed — Score: <span style={{ color: score >= 70 ? '#4caf50' : score >= 40 ? '#ff9800' : '#f44336', fontWeight: 700 }}>{score}%</span></div>
            </div>
            <div style={{ ...S.progress(score), marginBottom: 20 }}>
              <div style={S.progressBar(score, score >= 70 ? '#4caf50' : score >= 40 ? '#ff9800' : '#f44336')} />
            </div>
            {categories.map(cat => (
              <div key={cat} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#999', marginBottom: 8, textTransform: 'uppercase' }}>{cat}</div>
                {SEO_CHECKLIST.filter(c => c.category === cat).map(item => (
                  <div key={item.id} style={S.checkItem(checklist[item.id])} onClick={() => toggleCheck(item.id)}>
                    <span style={{ fontSize: 18 }}>{checklist[item.id] ? '✅' : '⬜'}</span>
                    <span style={{ flex: 1, color: 'var(--text)' }}>{item.label}</span>
                    <span style={S.badge(item.weight >= 8 ? '#f44336' : item.weight >= 6 ? '#ff9800' : '#4caf50')}>
                      {item.weight >= 8 ? 'High' : item.weight >= 6 ? 'Medium' : 'Low'} Impact
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Keywords Tab */}
      {tab === 'keywords' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>Keyword Tracker</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Keyword</th>
                    <th style={S.th}>Search Volume</th>
                    <th style={S.th}>Difficulty</th>
                    <th style={S.th}>CPC</th>
                    <th style={S.th}>Position</th>
                    <th style={S.th}>Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {KEYWORD_DEMO.map((kw, i) => (
                    <tr key={i}>
                      <td style={{ ...S.td, fontWeight: 600 }}>{kw.keyword}</td>
                      <td style={S.td}>{kw.volume.toLocaleString()}</td>
                      <td style={S.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ ...S.progress(kw.difficulty), width: 60 }}>
                            <div style={S.progressBar(kw.difficulty, kw.difficulty > 50 ? '#f44336' : kw.difficulty > 30 ? '#ff9800' : '#4caf50')} />
                          </div>
                          <span>{kw.difficulty}</span>
                        </div>
                      </td>
                      <td style={S.td}>{kw.cpc}</td>
                      <td style={S.td}>
                        <span style={{ fontWeight: 700, color: kw.position <= 10 ? '#4caf50' : kw.position <= 20 ? '#ff9800' : '#f44336' }}>#{kw.position}</span>
                      </td>
                      <td style={S.td}>
                        <span>{kw.trend === 'up' ? '📈' : kw.trend === 'down' ? '📉' : '➡️'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Keyword Research</div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <input style={{ ...S.input, flex: 1 }} placeholder="Enter a seed keyword (e.g., garage door repair)" value={keywordInput} onChange={e => setKeywordInput(e.target.value)} />
              <button style={S.btn(true)}>🔍 Research</button>
            </div>
            <div style={{ fontSize: 13, color: '#888' }}>Enter a seed keyword to discover related terms, search volumes, and ranking opportunities. AI will suggest the best keywords for your business.</div>
          </div>
        </div>
      )}

      {/* Content Strategy Tab */}
      {tab === 'content' && (
        <div>
          <div style={{ ...S.tabs, marginBottom: 16 }}>
            <button style={S.tab(contentTab === 'ideas')} onClick={() => setContentTab('ideas')}>AI Content Ideas</button>
            <button style={S.tab(contentTab === 'calendar')} onClick={() => setContentTab('calendar')}>Content Calendar</button>
            <button style={S.tab(contentTab === 'outline')} onClick={() => setContentTab('outline')}>Blog Outline Generator</button>
          </div>

          {contentTab === 'ideas' && (
            <div style={S.card}>
              <div style={S.cardTitle}>AI-Suggested Content Ideas</div>
              <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>Based on your keywords and industry, here are high-impact content opportunities:</p>
              {CONTENT_IDEAS.map((idea, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderRadius: 8, border: '1px solid var(--border, #2a2a4a)', marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{idea.title}</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <span style={S.badge('#2196f3')}>{idea.type}</span>
                      <span style={S.badge(idea.difficulty === 'Easy' ? '#4caf50' : idea.difficulty === 'Medium' ? '#ff9800' : '#f44336')}>{idea.difficulty}</span>
                      <span style={{ fontSize: 11, color: '#888' }}>Est. {idea.est_traffic}</span>
                    </div>
                  </div>
                  <button style={S.btn(false)}>Generate Outline</button>
                </div>
              ))}
            </div>
          )}

          {contentTab === 'calendar' && (
            <div style={S.card}>
              <div style={S.cardTitle}>Content Calendar</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 16 }}>
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                  <div key={d} style={{ textAlign: 'center', fontSize: 11, color: '#888', fontWeight: 600, padding: 8 }}>{d}</div>
                ))}
                {Array.from({ length: 28 }, (_, i) => (
                  <div key={i} style={{ padding: 8, borderRadius: 6, border: '1px solid var(--border, #2a2a4a)', minHeight: 60, fontSize: 11, color: 'var(--text)' }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{i + 1}</div>
                    {i === 3 && <div style={{ ...S.badge('#2196f3'), fontSize: 9 }}>Blog Post</div>}
                    {i === 10 && <div style={{ ...S.badge('#4caf50'), fontSize: 9 }}>Video</div>}
                    {i === 17 && <div style={{ ...S.badge('#ff9800'), fontSize: 9 }}>Pillar Page</div>}
                    {i === 24 && <div style={{ ...S.badge('#9c27b0'), fontSize: 9 }}>Infographic</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {contentTab === 'outline' && (
            <div style={S.card}>
              <div style={S.cardTitle}>Blog Outline Generator</div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Topic / Title</label>
                <input style={S.input} placeholder="e.g., 10 Signs Your Garage Door Needs Replacement" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Target Keywords (comma-separated)</label>
                <input style={S.input} placeholder="garage door replacement, door repair signs" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Target Word Count</label>
                <select style={S.input}>
                  <option>500-800 (Short)</option>
                  <option>800-1500 (Medium)</option>
                  <option>1500-2500 (Long-form)</option>
                  <option>2500+ (Pillar Content)</option>
                </select>
              </div>
              <button style={S.btn(true)}>🤖 Generate Outline</button>
            </div>
          )}
        </div>
      )}

      {/* Meta Generator Tab */}
      {tab === 'meta' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>AI Meta Tag Generator</div>
            <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>Generate SEO-optimized title tags and meta descriptions for your pages.</p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Primary Keyword / Page Topic</label>
              <input style={S.input} placeholder="e.g., Overhead Door Installation" value={keywordInput} onChange={e => setKeywordInput(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Current Title Tag</label>
                <input style={S.input} placeholder="Your current page title" value={metaTitle} onChange={e => setMetaTitle(e.target.value)} />
                <div style={{ fontSize: 11, color: metaTitle.length > 60 ? '#f44336' : '#888', marginTop: 4 }}>{metaTitle.length}/60 characters</div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Current Meta Description</label>
                <input style={S.input} placeholder="Your current meta description" value={metaDesc} onChange={e => setMetaDesc(e.target.value)} />
                <div style={{ fontSize: 11, color: metaDesc.length > 160 ? '#f44336' : '#888', marginTop: 4 }}>{metaDesc.length}/160 characters</div>
              </div>
            </div>
            <button style={S.btn(true)} onClick={handleGenerateMeta} disabled={generating}>
              {generating ? '🤖 Generating...' : '🤖 Generate AI Suggestions'}
            </button>

            {generatedMeta && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>Suggested Titles</div>
                {generatedMeta.titles.map((t, i) => (
                  <div key={i} style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border, #2a2a4a)', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: 'var(--text)', fontSize: 14 }}>{t}</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: t.length <= 60 ? '#4caf50' : '#f44336' }}>{t.length} chars</span>
                      <button style={{ ...S.btn(false), padding: '4px 10px', fontSize: 11 }} onClick={() => setMetaTitle(t)}>Use</button>
                    </div>
                  </div>
                ))}
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 12, marginTop: 16 }}>Suggested Descriptions</div>
                {generatedMeta.descriptions.map((d, i) => (
                  <div key={i} style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border, #2a2a4a)', marginBottom: 8 }}>
                    <div style={{ color: 'var(--text)', fontSize: 13, marginBottom: 6 }}>{d}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: d.length <= 160 ? '#4caf50' : '#f44336' }}>{d.length} chars</span>
                      <button style={{ ...S.btn(false), padding: '4px 10px', fontSize: 11 }} onClick={() => setMetaDesc(d)}>Use</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Google Preview</div>
            <div style={{ padding: 20, borderRadius: 8, background: '#fff' }}>
              <div style={{ fontSize: 18, color: '#1a0dab', marginBottom: 4, fontFamily: 'Arial, sans-serif' }}>{metaTitle || 'Your Page Title — Business Name'}</div>
              <div style={{ fontSize: 14, color: '#006621', marginBottom: 4, fontFamily: 'Arial, sans-serif' }}>{siteUrl || 'https://yourbusiness.com'}/page</div>
              <div style={{ fontSize: 13, color: '#545454', fontFamily: 'Arial, sans-serif' }}>{metaDesc || 'Your meta description will appear here. Make it compelling to increase click-through rates from search results.'}</div>
            </div>
          </div>
        </div>
      )}

      {/* Competitors Tab */}
      {tab === 'competitors' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>Competitor SEO Analysis</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Domain</th>
                    <th style={S.th}>Domain Authority</th>
                    <th style={S.th}>Ranking Keywords</th>
                    <th style={S.th}>Backlinks</th>
                    <th style={S.th}>Est. Traffic</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPETITOR_DOMAINS.map((c, i) => (
                    <tr key={i}>
                      <td style={{ ...S.td, fontWeight: 600 }}>{c.domain}</td>
                      <td style={S.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ ...S.progress(c.da), width: 60 }}>
                            <div style={S.progressBar(c.da, '#2196f3')} />
                          </div>
                          <span>{c.da}</span>
                        </div>
                      </td>
                      <td style={S.td}>{c.keywords}</td>
                      <td style={S.td}>{c.backlinks}</td>
                      <td style={S.td}>{c.traffic}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Keyword Gap Analysis</div>
            <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>Keywords your competitors rank for that you don't:</p>
            {['smart garage door systems', 'insulated overhead doors', 'emergency garage door service 24/7', 'garage door spring replacement cost'].map((kw, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border, #2a2a4a)', marginBottom: 6 }}>
                <span style={{ color: 'var(--text)', fontWeight: 500 }}>{kw}</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: '#888' }}>Vol: {(1000 + i * 800).toLocaleString()}</span>
                  <button style={{ ...S.btn(true), padding: '4px 12px', fontSize: 11 }}>+ Track</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reports Tab */}
      {tab === 'reports' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>SEO Performance Reports</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Monthly SEO Report', desc: 'Rankings, traffic, and recommendations', icon: '📊' },
                { label: 'Backlink Report', desc: 'New and lost backlinks analysis', icon: '🔗' },
                { label: 'Technical Audit', desc: 'Site health and crawl issues', icon: '🔧' },
                { label: 'Content Performance', desc: 'Top pages and engagement metrics', icon: '📝' },
              ].map((r, i) => (
                <div key={i} style={{ padding: 16, borderRadius: 10, border: '1px solid var(--border, #2a2a4a)', cursor: 'pointer' }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>{r.icon}</div>
                  <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{r.label}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{r.desc}</div>
                  <button style={{ ...S.btn(true), marginTop: 10, fontSize: 11, padding: '6px 12px' }}>Generate</button>
                </div>
              ))}
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Report History</div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Report</th>
                  <th style={S.th}>Generated</th>
                  <th style={S.th}>Period</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { name: 'Monthly SEO Report', date: 'Feb 15, 2026', period: 'Jan 2026' },
                  { name: 'Technical Audit', date: 'Feb 10, 2026', period: 'Q1 2026' },
                  { name: 'Backlink Report', date: 'Feb 1, 2026', period: 'Jan 2026' },
                ].map((r, i) => (
                  <tr key={i}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{r.name}</td>
                    <td style={S.td}>{r.date}</td>
                    <td style={S.td}>{r.period}</td>
                    <td style={S.td}>
                      <button style={{ ...S.btn(false), padding: '4px 10px', fontSize: 11 }}>📥 Download</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default SEOOptimizerPage;
